import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  openRequestEvents,
  portalApi,
  type MergeMethod,
  type MergeReadinessItem,
  type PortalNotification,
  type PortalRepository,
  type PortalRequestDetail,
  type PortalRequestSummary,
  type PortalUser,
  type RequestMode,
  type RequestPlan,
  type RequestState,
  type TeamMember,
} from "./portal-api";
import {
  artifactsByKind,
  autopilotHeadline,
  canAcceptRequest,
  canApprovePlan,
  canCancelRequest,
  canCompleteInformationRequest,
  canDeleteRequest,
  canMergeFromPortal,
  canRequestChanges,
  compactSha,
  currentRequestStep,
  DEPLOYMENT_TIERS,
  deploymentReached,
  deploymentTierSummary,
  environmentTier,
  exactHeadEvidence,
  formatRelativeTime,
  furthestRequestStep,
  groupRequests,
  isAutoRequestActive,
  outstandingMergeGates,
  newPortalRepository,
  normalizePortalRepository,
  planLineDiff,
  PORTAL_QA_CONFIG_PATH,
  REQUEST_STEP_META,
  requestStepAvailable,
  requesterApprovedCurrentRevision,
  REQUEST_STATE_META,
  validatePortalRepository,
  visibleRequestGroups,
} from "./portal-data";
import {
  BoardSkeleton,
  PortalRouteSkeleton,
  PortalRowsSkeleton,
  PortalSkeletonBar,
  RequestDetailSkeleton,
} from "./portal-skeleton";
import {
  parsePortalRoute,
  portalNavigationState,
  portalRequestPath,
  portalReturnTo,
  requestBoardHistoryState,
  requestBoardTabFromState,
  type PortalRoute,
  type RequestBoardTab,
  type RequestStep,
} from "./portal-routing";
import { StatusPage } from "./status-page";
import netraKoiIcon from "./assets/netra-koi.jpg";
import "./portal-styles.css";

function navigate(path: string) {
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== path) {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.history.pushState(portalNavigationState(window.history.state, returnTo), "", path);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigateRequestStep(requestId: string, step: RequestStep, replace = false) {
  const path = portalRequestPath(requestId, step);
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` === path) return;
  if (replace) window.history.replaceState(window.history.state, "", path);
  else window.history.pushState(window.history.state, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function navigateBackFromRequest() {
  const returnTo = portalReturnTo(window.history.state);
  if (returnTo) {
    navigate(returnTo);
    return;
  }
  navigate("/requests");
}

function stateMeta(state: RequestState) {
  return REQUEST_STATE_META[state] ?? { label: state.replaceAll("_", " "), group: "in_progress", tone: "blue" };
}

function errorMessage(reason: unknown) {
  return reason instanceof Error ? reason.message : "Netra could not complete that request.";
}

function fileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

function attachmentContentType(file: File) {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase();
  return ({ pdf: "application/pdf", txt: "text/plain", csv: "text/csv", json: "application/json" } as Record<string, string>)[extension ?? ""] ?? "application/octet-stream";
}

const PASTE_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);

type ClipboardItem = Pick<DataTransferItem, "kind" | "type" | "getAsFile">;

export function pastedImagesFromClipboard(items: Iterable<ClipboardItem>, existingAttachmentCount: number) {
  const imageItems = Array.from(items).filter((item) => item.kind === "file" && item.type.startsWith("image/"));
  if (imageItems.length === 0) return { handled: false, files: [] as File[] };
  if (imageItems.some((item) => !PASTE_IMAGE_TYPES.has(item.type))) {
    return { handled: true, files: [] as File[], error: "Paste a PNG, JPEG, WebP, or GIF image." };
  }
  const files = imageItems.map((item) => item.getAsFile()).filter((file): file is File => Boolean(file));
  if (files.length === 0) return { handled: true, files, error: "The pasted image could not be read." };
  if (files.some((file) => file.size > 5 * 1024 * 1024)) {
    return { handled: true, files: [] as File[], error: "Each image must be 5 MB or smaller." };
  }
  if (existingAttachmentCount + files.length > 20) {
    return { handled: true, files: [] as File[], error: "A request can contain at most 20 attachments." };
  }
  return { handled: true, files };
}

function pasteImages(
  event: React.ClipboardEvent<HTMLTextAreaElement>,
  existingAttachmentCount: number,
  setFiles: React.Dispatch<React.SetStateAction<File[]>>,
  onError: (error: string) => void,
) {
  const pasted = pastedImagesFromClipboard(event.clipboardData.items, existingAttachmentCount);
  if (!pasted.handled) return;
  event.preventDefault();
  if (pasted.error) onError(pasted.error);
  else setFiles((current) => [...current, ...pasted.files]);
}

function PendingUpload({ file, icon, onRemove }: { file: File; icon: string; onRemove: () => void }) {
  const isImage = PASTE_IMAGE_TYPES.has(file.type);
  const [previewUrl, setPreviewUrl] = useState("");
  useEffect(() => {
    if (!isImage) return;
    let active = true;
    const reader = new FileReader();
    reader.onload = () => {
      if (active && typeof reader.result === "string") setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
    return () => {
      active = false;
      if (reader.readyState === FileReader.LOADING) reader.abort();
    };
  }, [file, isImage]);
  return <span className={isImage ? "portal-upload-item image" : "portal-upload-item"}>
    {isImage ? previewUrl
      ? <img className="portal-upload-thumbnail" src={previewUrl} alt="" />
      : <span className="portal-upload-thumbnail placeholder" aria-hidden="true">▧</span>
      : <i aria-hidden="true">{icon}</i>}
    <span className="portal-upload-name">{file.name}</span>
    <button type="button" aria-label={`Remove ${file.name}`} onClick={onRemove}>×</button>
  </span>;
}

export function PendingUploadList({ files, icon = "⌕", onRemove }: { files: File[]; icon?: string; onRemove: (index: number) => void }) {
  if (files.length === 0) return null;
  return <div className="portal-upload-list">{files.map((file, index) => <PendingUpload
    key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
    file={file}
    icon={icon}
    onRemove={() => onRemove(index)}
  />)}</div>;
}

async function openArtifact(artifactId: string, onError: (message: string) => void) {
  const popup = window.open("", "_blank");
  if (popup) {
    popup.opener = null;
    popup.document.title = "Opening Netra evidence…";
    popup.document.body.textContent = "Opening private evidence…";
  }
  try {
    const access = await portalApi.accessArtifact(artifactId);
    if (popup) popup.location.replace(access.url);
    else onError("Allow pop-ups to open this private evidence artifact.");
  } catch (reason) {
    popup?.close();
    onError(errorMessage(reason));
  }
}

async function signOut() {
  await fetch("/auth/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { Origin: window.location.origin, "X-Netra-Portal-CSRF": "1" },
  }).catch(() => undefined);
  window.location.assign("/auth/login?return_to=%2Frequests");
}

function PrivateEvidenceMedia({ artifact, kind, onError }: {
  artifact: PortalRequestDetail["artifacts"][number];
  kind: "video" | "screenshot";
  onError: (message: string) => void;
}) {
  const [url, setUrl] = useState(artifact.url ?? "");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const access = await portalApi.accessArtifact(artifact.id);
      setUrl(access.url);
    } catch (reason) {
      onError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };
  if (url && kind === "video") return <video controls autoPlay preload="metadata" src={url}>Your browser does not support video playback.</video>;
  if (url) return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={artifact.title} loading="lazy" /></a>;
  return <button className={kind === "video" ? "portal-artifact-open" : "portal-media-placeholder"} disabled={loading} onClick={() => void load()}>{loading ? "Authorizing…" : `Load private ${kind} ↗`}</button>;
}

function PortalMark() {
  return <span className="portal-mark" aria-hidden="true"><i /><i /><i /></span>;
}

function NetraAgentAvatar() {
  return <span className="portal-message-avatar portal-agent-avatar" aria-hidden="true"><img src={netraKoiIcon} alt="" /></span>;
}

export function StateBadge({ state }: { state: RequestState }) {
  const meta = stateMeta(state);
  return <span className={`portal-state ${meta.tone}`}>{meta.label}</span>;
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return <div className="portal-empty"><span aria-hidden="true">◇</span><strong>{title}</strong><p>{body}</p></div>;
}

export function usesEmbeddedControlRoomTheme(route: PortalRoute) {
  return ["requests", "request", "merge-readiness", "team"].includes(route.view);
}

/// `actor` is the signed-in user the shell has already resolved. The portal
/// used to ask `/me` for itself, which meant a member waited for that round
/// trip twice — once before this component could mount at all, and again
/// before it would start loading anything to show. It is still optional, so a
/// caller that has not resolved a user yet behaves as before.
export function PortalApp({ embedded = false, actor }: { embedded?: boolean; actor?: PortalUser }) {
  const [route, setRoute] = useState<PortalRoute>(() => typeof window === "undefined"
    ? { view: "requests" }
    : parsePortalRoute(window.location.pathname) ?? { view: "requests" });
  const [user, setUser] = useState<PortalUser | null>(actor ?? null);
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [repositories, setRepositories] = useState<PortalRepository[]>([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const onPopState = () => setRoute(parsePortalRoute(window.location.pathname) ?? { view: "requests" });
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      setNotifications(await portalApi.listNotifications());
    } catch {
      // Notifications must not make the main work surface unavailable.
    }
  }, []);

  const refreshRepositories = useCallback(async () => {
    try {
      setRepositories(await portalApi.listRepositories());
    } catch {
      // Staff can still view existing requests if repository settings are unavailable.
    }
  }, []);

  useEffect(() => {
    let active = true;
    // With an actor in hand the two loads start immediately; without one they
    // still wait on `/me`, because which of them to issue depends on the role.
    const resolved = actor ? Promise.resolve(actor) : portalApi.me();
    resolved
      .then(async (me) => {
        const [requestUpdates, repoList] = await Promise.all([
          portalApi.listNotifications().catch(() => []),
          me.role === "admin" ? portalApi.listRepositories().catch(() => []) : Promise.resolve([]),
        ]);
        if (!active) return;
        setUser(me);
        setNotifications(requestUpdates);
        setRepositories(repoList);
        setError("");
      })
      .catch((reason) => active && setError(errorMessage(reason)))
      .finally(() => active && setBootstrapLoading(false));
    return () => { active = false; };
  }, [actor]);

  useEffect(() => {
    if (!user) return;
    const timer = window.setInterval(() => void refreshNotifications(), 30_000);
    return () => window.clearInterval(timer);
  }, [refreshNotifications, user]);

  const markNotificationRead = async (notification: PortalNotification) => {
    if (!notification.read_at) {
      try {
        await portalApi.markNotificationRead(notification.id);
        setNotifications((current) => current.map((item) => item.id === notification.id
          ? { ...item, read_at: new Date().toISOString() }
          : item));
      } catch (reason) {
        setError(errorMessage(reason));
      }
    }
    if (notification.request_id) navigate(portalRequestPath(notification.request_id));
  };

  const content = <>
    {error && <div className="portal-banner error" role="alert"><span>{error}</span><button onClick={() => setError("")} aria-label="Dismiss error">×</button></div>}
    {/* The route is already known — it came out of the URL — so the wait is
        drawn in the shape of where the reader is going, and the view's own
        first load continues it rather than restarting it. */}
    {bootstrapLoading ? <PortalRouteSkeleton route={route} /> : <>
      {route.view === "requests" && <RequestBoard notifications={notifications} onNotificationSelect={(notification) => void markNotificationRead(notification)} onError={setError} />}
      {route.view === "request" && <RequestDetail requestId={route.requestId} routeStep={route.step} user={user} onError={setError} onNotificationsChanged={refreshNotifications} />}
      {route.view === "status" && <StatusPage statusWindow={route.statusWindow} onWindowChange={(next) => navigate(`/status/${next}`)} />}
      {route.view === "merge-readiness" && user?.role === "admin" && <MergeDesk user={user} onError={setError} />}
      {route.view === "repositories" && user?.role === "admin" && <RepositorySettings repositories={repositories} onChanged={refreshRepositories} onError={setError} />}
      {route.view === "team" && user?.role === "admin" && <TeamSettings currentUser={user} onError={setError} />}
    </>}
  </>;

  if (embedded) {
    const controlRoomView = usesEmbeddedControlRoomTheme(route);
    return <section className={`portal-embedded${controlRoomView ? " portal-control-room" : ""}`}>{content}</section>;
  }

  return <div className="portal-shell">
    <header className="portal-topbar">
      <button className="portal-brand" onClick={() => navigate("/requests")} aria-label="Netra requests home">
        <PortalMark />
        <span><strong>NETRA</strong><small>STAFF PORTAL</small></span>
      </button>
      <nav aria-label="Staff portal">
        <button className={route.view === "requests" || route.view === "request" ? "active" : ""} onClick={() => navigate("/requests")}>Requests</button>
        <button className={route.view === "status" ? "active" : ""} onClick={() => navigate("/status/30d")}>Status</button>
      </nav>
      <div className="portal-account">
        <span className="portal-avatar" aria-hidden="true">{(user?.name || user?.email || "N").slice(0, 1).toUpperCase()}</span>
        <span className="portal-account-copy"><strong>{user?.name ?? "Streamoid staff"}</strong><small>{user?.role ?? "staff"}</small></span>
        {user?.id !== "local-development" && <button className="portal-sign-out" onClick={() => void signOut()}>Sign out</button>}
      </div>
    </header>

    <main className="portal-main">{content}</main>
  </div>;
}

export function TeamSettings({ currentUser, onError }: { currentUser: PortalUser; onError: (message: string) => void }) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(() => portalApi.listTeam().then(setMembers).catch((reason) => onError(errorMessage(reason))).finally(() => setLoading(false)), [onError]);
  useEffect(() => { void refresh(); }, [refresh]);
  const update = async (target: string, role: TeamMember["role"], access: TeamMember["access"]) => {
    try { await portalApi.updateTeamMember(target, role, access); setEmail(""); await refresh(); }
    catch (reason) { onError(errorMessage(reason)); }
  };
  return <section><div className="portal-team-add"><input aria-label="Microsoft 365 email" placeholder="person@streamoid.com" value={email} onChange={(event) => setEmail(event.target.value)} /><button className="portal-primary" disabled={!email.includes("@") || loading} onClick={() => void update(email, "member", "active")}>Add member</button></div>
    <div className="portal-team-table"><div className="portal-team-row heading"><span>Person</span><span>Role</span><span>Access</span></div>{members.map((member) => <div className="portal-team-row" key={member.email}><span><strong>{member.display_name ?? member.email}</strong><small>{member.email}{member.last_signed_in_at ? ` · signed in ${formatRelativeTime(member.last_signed_in_at)}` : " · not signed in"}</small></span><select aria-label={`${member.email} role`} value={member.role} disabled={member.email === currentUser.email} onChange={(event) => void update(member.email, event.target.value as TeamMember["role"], member.access)}><option value="member">Member</option><option value="admin">Admin</option></select><select aria-label={`${member.email} access`} value={member.access} disabled={member.email === currentUser.email} onChange={(event) => void update(member.email, member.role, event.target.value as TeamMember["access"])}><option value="active">Has access</option><option value="denied">No access</option></select></div>)}</div>
  </section>;
}

export function RequestUpdates({ notifications, onSelect }: {
  notifications: PortalNotification[];
  onSelect: (notification: PortalNotification) => void;
}) {
  const unread = notifications.filter((item) => !item.read_at).length;
  return <section className="portal-request-updates" aria-label="Request updates">
    <header><h2>Updates</h2>{unread > 0 && <span>{unread} new</span>}</header>
    <div className="portal-request-updates-list">{notifications.length === 0 ? <p>No updates yet.</p> : notifications.slice(0, 12).map((notification) => <button
      key={notification.id}
      className={notification.read_at ? "read" : ""}
      onClick={() => onSelect(notification)}
    >
      <i aria-hidden="true" />
      <span><strong>{notification.title}</strong>{notification.body && <small>{notification.body}</small>}<time>{formatRelativeTime(notification.created_at)}</time></span>
    </button>)}</div>
  </section>;
}

export function RequestBoard({ notifications, onNotificationSelect, onError }: {
  notifications: PortalNotification[];
  onNotificationSelect: (notification: PortalNotification) => void;
  onError: (error: string) => void;
}) {
  const [requests, setRequests] = useState<PortalRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<RequestBoardTab>(() => typeof window === "undefined"
    ? "ask"
    : requestBoardTabFromState(window.history.state));
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const refreshSequence = useRef(0);
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [creating, setCreating] = useState(false);
  const [mode, setMode] = useState<RequestMode>(readRequestMode);
  const chooseMode = (next: RequestMode) => {
    setMode(next);
    try { window.localStorage.setItem(REQUEST_MODE_STORAGE_KEY, next); } catch { /* storage is a convenience only */ }
  };

  const refresh = useCallback(async () => {
    const sequence = ++refreshSequence.current;
    setLoading(true);
    try {
      const result = await portalApi.listRequestsPage(page, search);
      if (sequence !== refreshSequence.current) return;
      setRequests(result.items);
      setTotal(result.total);
      onError("");
    } catch (reason) {
      if (sequence === refreshSequence.current) onError(errorMessage(reason));
    } finally {
      if (sequence === refreshSequence.current) setLoading(false);
    }
  }, [onError, page, search]);

  useEffect(() => { void refresh(); }, [refresh]);
  const selectTab = (tab: RequestBoardTab) => {
    window.history.replaceState(requestBoardHistoryState(window.history.state, tab), "");
    setActiveTab(tab);
  };
  const groups = visibleRequestGroups(groupRequests(requests), true);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = description.trim();
    const sourceLink = link.trim();
    if (!text && !sourceLink && images.length === 0 && attachments.length === 0) return;
    if (sourceLink) {
      try {
        const parsed = new URL(sourceLink);
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
      } catch {
        onError("Use a complete http:// or https:// link.");
        return;
      }
    }
    const titleSource = text.split("\n").find(Boolean) || sourceLink || images[0]?.name || attachments[0]?.name || "New request";
    const title = titleSource.replace(/^https?:\/\//, "").slice(0, 160);
    const files = [...images, ...attachments];
    const context = [text, sourceLink && `Link: ${sourceLink}`, ...files.map((file) => `Attached file: ${file.name}`)].filter(Boolean).join("\n\n");
    setCreating(true);
    try {
      const request = await portalApi.createRequest({
        repository: "",
        title,
        problem: text || sourceLink || `See attached file: ${files[0].name}`,
        reproduction: "",
        expected_behavior: "",
        actual_behavior: "",
        impact: "",
        environment: "",
        scope: "",
        acceptance_criteria: [],
        initial_message: context,
        defer_agent: true,
        ...(mode === "auto" ? { mode } : {}),
      });
      const failedUploads: string[] = [];
      const initialMessageId = request.messages.find((item) => item.author.role !== "agent" && item.author.role !== "system")?.id;
      for (const file of files) {
        try {
          await portalApi.uploadAttachment(request.id, {
            message_id: initialMessageId,
            file_name: file.name,
            content_type: attachmentContentType(file),
            data_base64: await fileAsBase64(file),
          });
        } catch {
          failedUploads.push(file.name);
        }
      }
      await portalApi.analyzeRequest(request.id);
      if (failedUploads.length > 0) onError(`Request created, but ${failedUploads.join(", ")} could not be uploaded.`);
      navigate(portalRequestPath(request.id));
    } catch (reason) {
      onError(errorMessage(reason));
    } finally {
      setCreating(false);
    }
  };

  return <>
    <nav className="detail-tabs section-tabs portal-request-tabs" aria-label="Requests views">
      <button className={activeTab === "ask" ? "active" : ""} aria-current={activeTab === "ask" ? "page" : undefined} onClick={() => selectTab("ask")}>Ask Netra</button>
      <button className={activeTab === "activity" ? "active" : ""} aria-current={activeTab === "activity" ? "page" : undefined} onClick={() => selectTab("activity")}>Updates{notifications.some((item) => !item.read_at) && <i aria-label="Unread updates" />}</button>
      <button className={activeTab === "board" ? "active" : ""} aria-current={activeTab === "board" ? "page" : undefined} onClick={() => selectTab("board")}>All requests</button>
    </nav>

    {activeTab === "ask" ? <section className="portal-ask-home">
      <div className="portal-ask-intro"><h2>{mode === "auto" ? "What should Netra fix?" : "What do you need help with?"}</h2></div>
      <RequestModeToggle mode={mode} onChange={chooseMode} />
      <form className={`portal-composer portal-simple-composer ${mode === "auto" ? "portal-auto-composer" : ""}`} onSubmit={submit}>
      <label className="portal-composer-body"><span className="sr-only">What do you need help with? You can paste an image.</span><textarea autoFocus value={description} onChange={(event) => setDescription(event.target.value)} onPaste={(event) => pasteImages(event, images.length + attachments.length, setImages, onError)} placeholder={mode === "auto" ? "Describe the change. For example: The upload button on Catalogix does nothing, or Rename the ‘Workspace’ label to ‘Project’ everywhere…" : "For example: What is the latest Photogenix version? or The upload button is not working…"} rows={6} /></label>
      {linkOpen && <label className="portal-composer-link"><span>Link</span><input type="url" value={link} onChange={(event) => setLink(event.target.value)} placeholder="https://…" /></label>}
      <PendingUploadList files={images} icon="▧" onRemove={(index) => setImages((current) => current.filter((_, itemIndex) => itemIndex !== index))} />
      <PendingUploadList files={attachments} onRemove={(index) => setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))} />
      <div className="portal-composer-actions">
        <div><label className="portal-attach-button"><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={(event) => {
          const selected = Array.from(event.target.files ?? []);
          if (selected.some((image) => image.size > 5 * 1024 * 1024)) onError("Each image must be 5 MB or smaller.");
          else if (images.length + attachments.length + selected.length > 20) onError("A request can contain at most 20 attachments.");
          else setImages((current) => [...current, ...selected]);
          event.target.value = "";
        }} /><span>▧ Add images</span></label><label className="portal-attach-button"><input type="file" accept="application/pdf,text/plain,text/csv,application/json,.pdf,.txt,.csv,.json" multiple onChange={(event) => {
          const selected = Array.from(event.target.files ?? []);
          if (selected.some((file) => file.size > 5 * 1024 * 1024)) onError("Each attachment must be 5 MB or smaller.");
          else if (images.length + attachments.length + selected.length > 20) onError("A request can contain at most 20 attachments.");
          else setAttachments((current) => [...current, ...selected]);
          event.target.value = "";
        }} /><span>⌕ Attach files</span></label><button type="button" className="portal-add-link" onClick={() => setLinkOpen((open) => { if (open) setLink(""); return !open; })}>↗ {linkOpen ? "Remove link" : "Add link"}</button></div>
        <span>{mode === "auto" ? "No questions: Netra finds the repositories, makes the change, and opens the pull requests. You can paste an image directly into this box." : "Start with whatever you know. You can paste an image directly into this box."}</span>
        <button className="portal-primary" disabled={creating || (!description.trim() && !link.trim() && images.length === 0 && attachments.length === 0)}>{creating ? "Starting…" : mode === "auto" ? "Fix it →" : "Ask Netra →"}</button>
      </div>
      </form>
    </section> : activeTab === "activity" ?
      <RequestUpdates notifications={notifications} onSelect={onNotificationSelect} />
    : <>
    <section className="portal-board-toolbar">
      <label><span aria-hidden="true">⌕</span><input aria-label="Search requests" placeholder="Search requests or repositories" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></label>
      <span>Newest activity first</span>
      <button onClick={() => void refresh()}>Refresh</button>
    </section>

    {loading ? <BoardSkeleton /> : requests.length === 0 ? <EmptyState title={search.trim() ? "No matching requests" : "No requests on this page"} body={search.trim() ? "Try another search." : "Start a conversation in Ask Netra or return to an earlier page."} /> : <div className="portal-board" style={{ ["--portal-board-open-columns" as string]: String(Math.max(1, groups.filter((group) => group.key !== "complete").length)) } as React.CSSProperties}>
      {groups.map((group) => <section className={`portal-board-column ${group.key}`} key={group.key}>
        <header><div><h2>{group.label}</h2><p>{group.description}</p></div><span>{group.requests.length}</span></header>
        <div className="portal-card-stack">
          {group.requests.length === 0 ? <div className="portal-column-empty">Nothing here</div> : group.requests.map((request) => <RequestCard key={request.id} request={request} />)}
        </div>
      </section>)}
    </div>}
    <nav className="portal-board-toolbar portal-request-pagination" aria-label="Request pagination">
      <button disabled={loading || page === 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
      <span aria-live="polite">Page {page} of {Math.max(1, Math.ceil(total / 20))} · {total} requests</span>
      <button disabled={loading || page * 20 >= total} onClick={() => setPage((current) => current + 1)}>Next</button>
    </nav>
    </>}
  </>;
}

export function RequestCard({ request }: { request: PortalRequestSummary }) {
  if (request.mode === "auto") return <AutoRequestCard request={request} />;
  return <button className="portal-request-card" onClick={() => navigate(portalRequestPath(request.id))}>
    <div>{request.request_type === "information" ? <><span className="portal-state blue">Information</span>{request.state === "closed" && <span className="portal-state green">Completed</span>}</> : <StateBadge state={request.state} />}{request.unread_count ? <span className="portal-unread">{request.unread_count} new</span> : null}</div>
    <h3>{request.title}</h3>
    <p>{request.latest_message ?? "No conversation summary yet."}</p>
    <div className="portal-repo-line"><span aria-hidden="true">⌘</span><strong>{request.repository || "Repository pending"}</strong></div>
    <footer>
      <span className="portal-card-avatar">{request.requester.name.slice(0, 1).toUpperCase()}</span>
      <span>{request.requester.name}</span>
      {request.github?.pull_request_number && <span>PR #{request.github.pull_request_number}</span>}
      <time>{formatRelativeTime(request.updated_at)}</time>
    </footer>
  </button>;
}

const REQUEST_MODE_STORAGE_KEY = "netra.portal.request-mode";

function readRequestMode(): RequestMode {
  try {
    return window.localStorage.getItem(REQUEST_MODE_STORAGE_KEY) === "guided" ? "guided" : "auto";
  } catch {
    return "auto";
  }
}

export function RequestModeToggle({ mode, onChange }: { mode: RequestMode; onChange: (mode: RequestMode) => void }) {
  return <div className="portal-mode-toggle" role="radiogroup" aria-label="How Netra should work">
    <button type="button" role="radio" aria-checked={mode === "auto"} className={mode === "auto" ? "active" : ""} onClick={() => onChange("auto")}>
      <strong>Auto mode</strong><small>No questions. Netra opens the PRs.</small>
    </button>
    <button type="button" role="radio" aria-checked={mode === "guided"} className={mode === "guided" ? "active" : ""} onClick={() => onChange("guided")}>
      <strong>Guided</strong><small>Questions, a plan, and preview QA.</small>
    </button>
  </div>;
}

function autoTone(request: Pick<PortalRequestSummary, "state">) {
  if (request.state === "closed") return "green";
  if (request.state === "failed") return "red";
  if (request.state === "cancelled") return "muted";
  return "blue";
}

export function AutoBadge() {
  return <span className="portal-state portal-auto-badge">Auto</span>;
}

export function AutoRequestCard({ request }: { request: PortalRequestSummary }) {
  const pulls = request.autopilot?.pull_requests ?? [];
  const repositories = [...new Set(pulls.map((pull) => pull.repository.split("/").at(-1)))];
  return <button className={`portal-request-card portal-auto-card ${isAutoRequestActive(request) ? "active" : ""}`} onClick={() => navigate(portalRequestPath(request.id))}>
    <div><AutoBadge /><span className={`portal-state ${autoTone(request)}`}>{request.state === "closed" ? "Done" : REQUEST_STATE_META[request.state]?.label ?? request.state}</span>{request.unread_count ? <span className="portal-unread">{request.unread_count} new</span> : null}</div>
    <h3>{request.title}</h3>
    <p>{autopilotHeadline(request)}</p>
    <div className="portal-repo-line"><span aria-hidden="true">⌘</span><strong>{repositories.length > 0 ? repositories.join(", ") : request.state === "closed" ? "No repository changed" : "Finding repositories…"}</strong></div>
    <footer>
      <span className="portal-card-avatar">{request.requester.name.slice(0, 1).toUpperCase()}</span>
      <span>{request.requester.name}</span>
      {pulls.length > 0 && <span>{pulls.length} PR{pulls.length === 1 ? "" : "s"}</span>}
      <time>{formatRelativeTime(request.updated_at)}</time>
    </footer>
  </button>;
}

const AUTO_STEPS = [
  { key: "queued", label: "Queued" },
  { key: "running", label: "Working" },
  { key: "finalizing", label: "Opening PRs" },
  { key: "done", label: "Done" },
] as const;

function autoStepIndex(request: PortalRequestDetail) {
  if (["closed", "failed", "cancelled"].includes(request.state)) return 3;
  if (request.autopilot?.status === "finalizing") return 2;
  if (request.state === "building") return 1;
  return 0;
}

export function AutoRequestDetail({ request, connected, busy, canCancel, canDelete, onCancel, onDelete }: {
  request: PortalRequestDetail;
  connected: boolean;
  busy: string;
  canCancel: boolean;
  canDelete: boolean;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const run = request.autopilot;
  const pulls = run?.pull_requests ?? [];
  const active = isAutoRequestActive(request);
  const step = autoStepIndex(request);
  const requesterMessage = request.messages.find((message) => message.author.email.toLowerCase() === request.requester.email.toLowerCase());
  const tone = autoTone(request);
  return <>
    <button className="portal-back" onClick={navigateBackFromRequest}>← Back</button>
    <section className="portal-detail-heading">
      <div>
        <div className="portal-heading-meta"><AutoBadge /><span className={`portal-state ${tone}`}>{request.state === "closed" ? "Done" : REQUEST_STATE_META[request.state]?.label ?? request.state}</span><span>Request {request.id.slice(0, 8)}</span><span className={connected ? "live" : ""}>{connected ? "Live" : "Reconnecting"}</span></div>
        <h1>{request.title}</h1>
        <p>Raised by {request.requester.name} · updated {formatRelativeTime(request.updated_at)}</p>
      </div>
      <div className="portal-heading-actions">
        {pulls.length === 1 && <a className="portal-primary" href={pulls[0].url} target="_blank" rel="noreferrer">PR #{pulls[0].number} ↗</a>}
        {active && canCancel && <button className="portal-secondary portal-danger" disabled={Boolean(busy)} onClick={onCancel}>{busy === "cancel" ? "Cancelling…" : "Stop"}</button>}
        {canDelete && <button className="portal-secondary portal-danger" disabled={Boolean(busy)} onClick={onDelete}>{busy === "delete" ? "Deleting…" : "Delete request"}</button>}
      </div>
    </section>

    <section className={`portal-panel portal-auto-status ${tone}`} aria-live="polite">
      <div className="portal-auto-status-mark" aria-hidden="true">{active ? <i className="portal-auto-spinner" /> : request.state === "closed" ? "✓" : request.state === "failed" ? "!" : "–"}</div>
      <div>
        <p className="portal-eyebrow">AUTO MODE</p>
        <h2>{autopilotHeadline(request)}</h2>
        <p>{active
          ? "Nothing is needed from you. Netra reads every repository it can change, edits whichever ones the request needs, and opens a pull request in each. This page updates on its own."
          : request.state === "closed"
            ? pulls.length > 0 ? "Review and merge the pull requests in GitHub as usual. Netra never merges." : "Netra looked across the repositories and found nothing to change."
            : pulls.length > 0
              ? request.state === "failed"
                ? "Netra stopped partway. The pull requests below were already opened — review them before asking again, so the change is not raised twice."
                : "Stopped after the pull requests below were opened. Review them, or close them in GitHub if you no longer want the change."
              : request.state === "failed" ? "No pull request was opened. Start a new auto-mode request with more detail, or switch to Guided." : "This request was stopped before any pull request was opened."}</p>
      </div>
      <ol className="portal-auto-steps">{AUTO_STEPS.map((item, index) => <li key={item.key} className={index < step ? "complete" : index === step ? (active ? "current" : request.state === "closed" ? "complete" : "stopped") : ""}><i aria-hidden="true">{index < step || (index === step && request.state === "closed") ? "✓" : index + 1}</i><span>{item.label}</span></li>)}</ol>
    </section>

    {pulls.length > 0 && <section className="portal-panel portal-auto-pulls">
      <header><h2>Pull requests</h2><span>{pulls.length}</span></header>
      <ul>{pulls.map((pull) => <li key={pull.url}>
        <div><strong>{pull.repository}</strong><span>#{pull.number} · {pull.title}</span>{pull.base && <small>{pull.branch} → {pull.base}</small>}</div>
        <a className="portal-secondary" href={pull.url} target="_blank" rel="noreferrer">Open ↗</a>
      </li>)}</ul>
    </section>}

    {run?.summary && <section className="portal-panel portal-auto-summary"><p className="portal-eyebrow">WHAT NETRA DID</p><PortalMessageBody body={run.summary} /></section>}
    {run?.error && ["failed", "closed"].includes(request.state) && <section className="portal-panel portal-auto-summary portal-auto-error"><p className="portal-eyebrow">{request.state === "failed" ? "WHY IT STOPPED" : "NOTES ON SOME REPOSITORIES"}</p><PortalMessageBody body={run.error} /></section>}

    <section className="portal-panel portal-auto-summary"><p className="portal-eyebrow">YOUR REQUEST</p><PortalMessageBody body={requesterMessage?.body ?? request.requirements?.problem ?? request.title} />{request.attachments.length > 0 && <MessageAttachments attachments={request.attachments} />}</section>
  </>;
}

const INTAKE_FIELDS = [
  "repository", "title", "problem", "reproduction", "expected_behavior",
  "actual_behavior", "impact", "environment", "scope", "acceptance_criteria",
] as const;

function nextIntakeField(request: PortalRequestDetail | null) {
  if (!request || request.state !== "needs_input") return undefined;
  if (!request.repository_confirmed) return undefined;
  if (request.request_type === "information") return request.repository ? undefined : "repository";
  return INTAKE_FIELDS.find((field) => field === "acceptance_criteria"
    ? !request.requirements?.acceptance_criteria?.length
    : !String(request.requirements?.[field] ?? "").trim());
}

export function answerableIntakeField(
  request: PortalRequestDetail | null,
  user: PortalUser | null,
) {
  const field = nextIntakeField(request);
  const latest = request?.messages.at(-1);
  return field
    && user?.email.toLowerCase() === request?.requester.email.toLowerCase()
    && latest?.kind === "clarification"
    && ["agent", "system"].includes(latest.author.role ?? "")
    ? field
    : undefined;
}

export function RequestDetail({ requestId, routeStep, user, onError, onNotificationsChanged, initialRequest = null }: {
  requestId: string;
  routeStep?: RequestStep;
  user: PortalUser | null;
  onError: (error: string) => void;
  onNotificationsChanged: () => Promise<void>;
  /** Preload a request so tests can render the loaded view without patching React internals. */
  initialRequest?: PortalRequestDetail | null;
}) {
  const [request, setRequest] = useState<PortalRequestDetail | null>(initialRequest);
  const [loading, setLoading] = useState(!initialRequest);
  const [connected, setConnected] = useState(false);
  const [message, setMessage] = useState("");
  const [messageLink, setMessageLink] = useState("");
  const [messageLinkOpen, setMessageLinkOpen] = useState(false);
  const [messageFiles, setMessageFiles] = useState<File[]>([]);
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [planView, setPlanView] = useState<"plan" | "diff">("plan");
  const [changeBody, setChangeBody] = useState("");
  const [changesOpen, setChangesOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const refresh = useCallback(async () => {
    try {
      const result = await portalApi.getRequest(requestId);
      setRequest(result);
      setSelectedPlan((current) => current || result.plans.at(-1)?.version || 0);
      onError("");
    } catch (reason) {
      onError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  }, [requestId, onError]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => openRequestEvents(requestId, () => {
    void refresh();
    void onNotificationsChanged();
  }, setConnected), [requestId, refresh, onNotificationsChanged]);

  useEffect(() => {
    if (!request || request.mode === "auto") return;
    if (routeStep === "qa" && request.state === "merge_ready" && requesterApprovedCurrentRevision(request)) {
      navigateRequestStep(requestId, "merge", true);
      return;
    }
    // The merge is the last human step. Once it lands, the request follows its
    // revision to the deployment step instead of waiting to be clicked through.
    if (routeStep === "merge" && request.state === "merged") {
      navigateRequestStep(requestId, "deploy", true);
      return;
    }
    const fallback = currentRequestStep(request);
    if (!routeStep || !requestStepAvailable(request, routeStep)) {
      navigateRequestStep(requestId, fallback, true);
    }
  }, [request, requestId, routeStep]);

  const runAction = async (name: string, action: () => Promise<PortalRequestDetail | unknown>) => {
    setBusy(name);
    try {
      const result = await action();
      if (result && typeof result === "object" && "id" in result && "state" in result) {
        setRequest(result as PortalRequestDetail);
        onError("");
        return result as PortalRequestDetail;
      }
      await refresh();
      onError("");
      return null;
    } catch (reason) {
      onError(errorMessage(reason));
      return null;
    } finally {
      setBusy("");
    }
  };

  const sendMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    const sourceLink = messageLink.trim();
    if (sourceLink) {
      try {
        const parsed = new URL(sourceLink);
        if (!["http:", "https:"].includes(parsed.protocol)) throw new Error();
      } catch {
        onError("Use a complete http:// or https:// link.");
        return;
      }
    }
    const text = message.trim();
    const body = [text, sourceLink].filter(Boolean).join("\n\n") || `Attached file: ${messageFiles[0]?.name ?? "attachment"}`;
    if (!text && !sourceLink && messageFiles.length === 0) return;
    setBusy("message");
    try {
      const sent = await portalApi.addMessage(requestId, body, answerableIntakeField(request, user), request?.request_type === "information");
      const failedUploads: string[] = [];
      for (const file of messageFiles) {
        try {
          await portalApi.uploadAttachment(requestId, {
            message_id: sent.id,
            file_name: file.name,
            content_type: attachmentContentType(file),
            data_base64: await fileAsBase64(file),
          });
        } catch {
          failedUploads.push(file.name);
        }
      }
      await portalApi.analyzeRequest(requestId);
      setMessage("");
      setMessageLink("");
      setMessageLinkOpen(false);
      setMessageFiles([]);
      await refresh();
      if (failedUploads.length > 0) onError(`Message sent, but ${failedUploads.join(", ")} could not be uploaded.`);
    } catch (reason) {
      onError(errorMessage(reason));
    } finally {
      setBusy("");
    }
  };

  const deleteCurrentRequest = async () => {
    if (!request || !window.confirm(`Delete “${request.title}”? This cannot be undone.`)) return;
    setBusy("delete");
    try {
      await portalApi.deleteRequest(request.id);
      onError("");
      navigate("/requests");
    } catch (reason) {
      onError(errorMessage(reason));
      setBusy("");
    }
  };

  const changeRepository = () => {
    if (!request) return;
    const repository = window.prompt(
      "Repository (owner/name)",
      request.repository,
    )?.trim();
    if (!repository || repository === request.repository) return;
    const requirements = request.requirements ?? {};
    void runAction("repository", () => portalApi.updateRequestRequirements(request.id, {
      repository,
      title: requirements.title ?? request.title,
      problem: requirements.problem ?? "",
      reproduction: requirements.reproduction ?? "",
      expected_behavior: requirements.expected_behavior ?? "",
      actual_behavior: requirements.actual_behavior ?? "",
      impact: requirements.impact ?? "",
      environment: requirements.environment ?? "",
      scope: requirements.scope ?? "",
      acceptance_criteria: requirements.acceptance_criteria ?? [],
    }));
  };

  const confirmRepository = () => {
    if (!request) return;
    void runAction("confirm-repository", () => portalApi.confirmRepository(request.id));
  };

  const cancelCurrentRequest = async () => {
    if (!request || !window.confirm("Cancel this request and stop any active Netra work?")) return;
    await runAction("cancel", () => portalApi.cancelRequest(request.id));
  };

  if (loading) return <RequestDetailSkeleton />;
  if (!request) return <EmptyState title="Request unavailable" body="The request could not be loaded or you do not have access." />;
  if (request.mode === "auto") {
    return <AutoRequestDetail
      request={request}
      connected={connected}
      busy={busy}
      canCancel={canCancelRequest(user, request)}
      canDelete={canDeleteRequest(user, request)}
      onCancel={() => void cancelCurrentRequest()}
      onDelete={() => void deleteCurrentRequest()}
    />;
  }

  const selected = request.plans.find((plan) => plan.version === selectedPlan) ?? request.plans.at(-1);
  const proof = exactHeadEvidence(request);
  const evidence = artifactsByKind(request.artifacts);
  const canOperate = user?.role === "operator" || user?.role === "admin";
  const canDelete = canDeleteRequest(user, request);
  const isInformation = request.request_type === "information";
  const canComplete = canCompleteInformationRequest(user, request);
  const canCancel = canCancelRequest(user, request);
  const canChanges = canRequestChanges(request);
  const answerPending = informationAnswerPending(request);
  const latestCycle = request.cycles.at(-1);
  const qaRetryable = Boolean(
    latestCycle?.stages?.some((stage) => stage.name === "Preview" && stage.status === "passed")
    && !latestCycle?.stages?.some((stage) => stage.name === "QA" && stage.status === "passed")
    && request.state === "failed",
  );
  const activeStep = routeStep && requestStepAvailable(request, routeStep)
    ? routeStep
    : currentRequestStep(request);

  return <>
    <button className="portal-back" onClick={navigateBackFromRequest}>← Back</button>
    <section className="portal-detail-heading">
      <div>
        <div className="portal-heading-meta">{isInformation ? <><span className="portal-state blue">Information</span>{request.state === "closed" && <span className="portal-state green">Completed</span>}</> : <StateBadge state={request.state} />}<span>{request.repository || "Product pending"}</span>{["needs_input", "plan_approval"].includes(request.state) && <button type="button" className="portal-inline-action" disabled={busy === "repository"} onClick={changeRepository}>{busy === "repository" ? "Changing…" : "Change repository"}</button>}<span>Request {request.id.slice(0, 8)}</span><span className={connected ? "live" : ""}>{connected ? "Live" : "Reconnecting"}</span></div>
        <h1>{request.title}</h1>
        <p>Raised by {request.requester.name} · updated {formatRelativeTime(request.updated_at)}</p>
      </div>
      <div className="portal-heading-actions">
        {request.github?.issue_url && <a className="portal-secondary" href={request.github.issue_url} target="_blank" rel="noreferrer">Issue #{request.github.issue_number} ↗</a>}
        {request.github?.pull_request_url && <a className="portal-primary" href={request.github.pull_request_url} target="_blank" rel="noreferrer">PR #{request.github.pull_request_number} ↗</a>}
        {canOperate && (["failed", "merge_blocked"].includes(request.state) || qaRetryable) && <button className="portal-secondary" disabled={Boolean(busy)} onClick={() => void runAction("retry", () => portalApi.retryRequest(request.id)).then((result) => { if (result && result.cycles.at(-1)?.stages?.some((stage) => stage.name === "QA" && ["pending", "running"].includes(stage.status))) navigateRequestStep(request.id, "qa"); })}>{busy === "retry" ? "Retrying…" : qaRetryable ? "Retry QA" : "Retry stage"}</button>}
        {canComplete && <button className="portal-primary" disabled={Boolean(busy) || answerPending} title={answerPending ? "Wait for Netra to finish the current answer." : undefined} onClick={() => void runAction("complete", () => portalApi.completeRequest(request.id))}>{busy === "complete" ? "Completing…" : "Mark complete"}</button>}
        {canCancel && <button className="portal-secondary portal-danger" disabled={Boolean(busy)} onClick={() => void cancelCurrentRequest()}>{busy === "cancel" ? "Cancelling…" : "Cancel request"}</button>}
        {canDelete && <button className="portal-secondary portal-danger" disabled={Boolean(busy)} onClick={() => void deleteCurrentRequest()}>{busy === "delete" ? "Deleting…" : "Delete request"}</button>}
      </div>
    </section>

    {!request.repository_confirmed && request.repository && <section className="portal-repository-confirmation" aria-label="Confirm repository">
      <div><p className="portal-eyebrow">NETRA’S MATCH</p><h2>Is this the correct repository?</h2><p>Netra classified this request as <strong>{isInformation ? "an information question" : "work to be completed"}</strong> and matched it to <code>{request.repository}</code>.</p></div>
      <div><button type="button" className="portal-primary" disabled={Boolean(busy)} onClick={confirmRepository}>{busy === "confirm-repository" ? "Confirming…" : "Yes, use this repository"}</button><button type="button" className="portal-secondary" disabled={Boolean(busy)} onClick={changeRepository}>Change repository</button></div>
    </section>}

    {isInformation ? <div className="portal-information-detail"><Conversation request={request} message={message} setMessage={setMessage} messageLink={messageLink} setMessageLink={setMessageLink} messageLinkOpen={messageLinkOpen} setMessageLinkOpen={setMessageLinkOpen} files={messageFiles} setFiles={setMessageFiles} busy={busy} onError={onError} onSubmit={sendMessage} /></div> : <>
      <Lifecycle request={request} activeStep={activeStep} onSelect={(step) => navigateRequestStep(request.id, step)} />
      <div className="portal-step-page">
        {activeStep === "request" && <div className="portal-detail-grid"><div className="portal-detail-primary"><Conversation request={request} message={message} setMessage={setMessage} messageLink={messageLink} setMessageLink={setMessageLink} messageLinkOpen={messageLinkOpen} setMessageLinkOpen={setMessageLinkOpen} files={messageFiles} setFiles={setMessageFiles} busy={busy} onError={onError} onSubmit={sendMessage} /></div><aside className="portal-detail-sidebar"><RequirementsPanel request={request} /></aside></div>}
        {activeStep === "plan" && <div className="portal-detail-grid"><div className="portal-detail-primary"><PlanPanel request={request} selected={selected} selectedVersion={selectedPlan} setSelectedVersion={setSelectedPlan} planView={planView} setPlanView={setPlanView} busy={busy} onApprove={() => selected && void runAction("approve", () => portalApi.approvePlan(request.id, selected.version)).then((result) => { if (result && ["queued", "building", "previewing", "verifying", "awaiting_feedback"].includes(result.state)) navigateRequestStep(request.id, "delivery"); })} /></div><aside className="portal-detail-sidebar"><RequirementsPanel request={request} /></aside></div>}
        {activeStep === "delivery" && <DeliveryStage request={request} proof={proof} />}
        {activeStep === "qa" && <div className="portal-detail-grid"><div className="portal-detail-primary"><EvidencePanel request={request} proof={proof} videos={evidence.videos} screenshots={evidence.screenshots} reports={evidence.reports} busy={busy} onApprove={() => void runAction("accept", () => portalApi.acceptRequest(request.id)).then((result) => { if (result?.state === "merge_ready") navigateRequestStep(request.id, "merge"); })} onError={onError} /></div><aside className="portal-detail-sidebar"><section className="portal-panel portal-decision-panel"><p className="portal-eyebrow">YOUR DECISION</p><h2>Review this revision</h2><p>{requesterApprovedCurrentRevision(request) ? "You marked this exact preview as looking good. Netra will finish the automated evidence gates before the revision becomes merge-ready." : "Open the exact-head preview, try the acceptance steps, then choose Looks good or request changes."}</p><button className="portal-secondary" disabled={Boolean(busy) || !canChanges} title={canChanges ? undefined : "An exact-head preview must be available first."} onClick={() => setChangesOpen((open) => !open)}>Request changes</button>{changesOpen && <form onSubmit={(event) => { event.preventDefault(); const body = changeBody.trim(); if (!body) return; void runAction("changes", () => portalApi.requestChanges(request.id, body)).then((result) => { setChangeBody(""); setChangesOpen(false); if (result) navigateRequestStep(request.id, "plan"); }); }}><label><span>What should change?</span><textarea rows={4} value={changeBody} onChange={(event) => setChangeBody(event.target.value)} placeholder="Be specific about the desired result." required /></label><button className="portal-primary" disabled={busy === "changes" || !changeBody.trim()}>{busy === "changes" ? "Submitting…" : "Submit for a new plan"}</button></form>}</section><DeliveryPanel request={request} proof={proof} /></aside></div>}
        {activeStep === "merge" && <MergeStage request={request} proof={proof} user={user} busy={busy} onMerge={(method) => void runAction("merge", () => portalApi.mergeRequest(request.id, method)).then((result) => { if (result?.state === "merged") navigateRequestStep(request.id, "deploy"); })} />}
        {activeStep === "deploy" && <DeployStage request={request} />}
      </div>
      <StepFooter request={request} activeStep={activeStep} onSelect={(step) => navigateRequestStep(request.id, step)} />
    </>}
  </>;
}

export function Lifecycle({ request, activeStep = currentRequestStep(request), onSelect }: { request: PortalRequestDetail; activeStep?: RequestStep; onSelect?: (step: RequestStep) => void }) {
  const currentIndex = REQUEST_STEP_META.findIndex((step) => step.key === currentRequestStep(request));
  const activeIndex = REQUEST_STEP_META.findIndex((step) => step.key === activeStep);
  const guidance = REQUEST_STEP_META[activeIndex] ?? REQUEST_STEP_META[0];
  const cycleNumber = request.cycles.at(-1)?.number ?? 0;
  return <section className="portal-lifecycle portal-workflow-steps" aria-label="Request workflow">
    <div className="portal-lifecycle-summary"><span>Step {activeIndex + 1} of {REQUEST_STEP_META.length}</span><strong>{guidance.label}</strong><p>{guidance.description}</p><small>Workflow cycle {cycleNumber}</small></div>
    <ol>{REQUEST_STEP_META.map((step, index) => { const available = requestStepAvailable(request, step.key); return <li key={step.key} className={`${index < currentIndex ? "complete" : index === currentIndex ? "current" : ""} ${index === activeIndex ? "selected" : ""} ${available ? "" : "locked"}`}><button disabled={!available} aria-current={index === activeIndex ? "step" : undefined} onClick={() => onSelect?.(step.key)}><i aria-hidden="true">{index < currentIndex ? "✓" : index + 1}</i><span>{step.label}</span></button></li>; })}</ol>
  </section>;
}

export function Conversation({ request, message, setMessage, messageLink, setMessageLink, messageLinkOpen, setMessageLinkOpen, files, setFiles, busy, onError, onSubmit }: {
  request: PortalRequestDetail;
  message: string;
  setMessage: (message: string) => void;
  messageLink: string;
  setMessageLink: (link: string) => void;
  messageLinkOpen: boolean;
  setMessageLinkOpen: (open: boolean) => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  busy: string;
  onError: (error: string) => void;
  onSubmit: (event: React.FormEvent) => void;
}) {
  const isInformation = request.request_type === "information";
  const answerPending = informationAnswerPending(request);
  const complete = ["merged", "closed", "cancelled"].includes(request.state);
  return <section className="portal-panel portal-conversation">
    <div className="portal-panel-heading"><div><p className="portal-eyebrow">CONVERSATION</p><h2>{isInformation ? "Your question" : "Problem and decisions"}</h2></div><span>{request.messages.length} messages</span></div>
    <div className="portal-message-list">
      {request.messages.length === 0 ? <EmptyState title="Start the conversation" body={isInformation ? "Ask Netra anything about the selected product." : "Add context so Netra can prepare a complete plan."} /> : request.messages.map((item) => <article key={item.id} className={item.author.role === "agent" || item.author.role === "system" ? "agent" : "human"}>
        {item.author.role === "agent" || item.author.role === "system" ? <NetraAgentAvatar /> : <span className="portal-message-avatar">{item.author.name.slice(0, 1).toUpperCase()}</span>}
        <div><header><strong>{item.author.name}</strong><span>{item.author.role ?? "participant"}</span><time>{formatRelativeTime(item.created_at)}</time></header><PortalMessageBody body={item.body} /><MessageAttachments attachments={request.attachments.filter((attachment) => attachment.message_id === item.id)} /></div>
      </article>)}
      {answerPending && <InformationAnswerProgress repository={request.repository} />}
      {!isInformation && request.intake_analysis_pending && <IntakeAnalysisProgress repository={request.repository} />}
      <MessageAttachments attachments={request.attachments.filter((attachment) => !attachment.message_id)} />
    </div>
    {complete ? <div className="portal-message-composer"><span>This request is complete. Its conversation has been retained.</span></div> : <form className="portal-message-composer" onSubmit={onSubmit}>
      <label><span className="sr-only">Message Netra and the request participants. You can paste an image.</span><textarea rows={3} value={message} onChange={(event) => setMessage(event.target.value)} onPaste={(event) => pasteImages(event, request.attachments.length + files.length, setFiles, onError)} disabled={answerPending} placeholder={answerPending ? "Netra is working on your answer…" : nextIntakeField(request) ? "Answer Netra’s question…" : isInformation ? "Ask a follow-up question…" : "Add context or mention a constraint…"} /></label>
      {messageLinkOpen && <label className="portal-message-link"><span className="sr-only">Link to include</span><input type="url" value={messageLink} onChange={(event) => setMessageLink(event.target.value)} placeholder="https://…" /></label>}
      <PendingUploadList files={files} onRemove={(index) => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} />
      <div className="portal-message-actions"><div><label className="portal-attach-button"><input type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,application/json,.pdf,.txt,.csv,.json" multiple onChange={(event) => {
        const selected = Array.from(event.target.files ?? []);
        if (selected.some((file) => file.size > 5 * 1024 * 1024)) onError("Each attachment must be 5 MB or smaller.");
        else if (request.attachments.length + files.length + selected.length > 20) onError("A request can contain at most 20 attachments.");
        else setFiles((current) => [...current, ...selected]);
        event.target.value = "";
      }} /><span>⌕ Attach</span></label><button type="button" className="portal-add-link" onClick={() => { setMessageLinkOpen(!messageLinkOpen); if (messageLinkOpen) setMessageLink(""); }}>↗ {messageLinkOpen ? "Remove link" : "Add link"}</button></div><span>{answerPending ? "The follow-up box will reopen as soon as Netra posts the answer." : nextIntakeField(request) ? "Answer in your own words. Netra will organize the details." : isInformation ? "Netra will answer here and keep the context for follow-ups." : "Shared with everyone who can access this request."}</span><button className="portal-primary" disabled={answerPending || busy === "message" || (!message.trim() && !messageLink.trim() && files.length === 0)}>{busy === "message" ? "Sending…" : "Send message"}</button></div>
    </form>}
  </section>;
}

export function informationAnswerPending(request: PortalRequestDetail) {
  if (request.request_type !== "information") return false;
  if (["cancelled", "closed", "merged"].includes(request.state)) return false;
  if (request.information_answer_pending) return true;
  let latestRequesterIndex = -1;
  for (let index = request.messages.length - 1; index >= 0; index -= 1) {
    const role = request.messages[index].author.role;
    if (role !== "agent" && role !== "system") {
      latestRequesterIndex = index;
      break;
    }
  }
  if (latestRequesterIndex < 0) return false;
  const responses = request.messages.slice(latestRequesterIndex + 1);
  if (responses.some((item) => item.kind === "information_answer")) return false;
  // A clarification means Netra is waiting for the requester, not generating an answer.
  if (responses.some((item) => item.kind === "clarification")) return false;
  return true;
}

export function InformationAnswerProgress({ repository = "" }: { repository?: string }) {
  const repositoryName = repository.split("/").at(-1)?.replaceAll("-", " ") || "the selected product";
  return <article className="agent portal-message-progress" role="status" aria-live="polite">
    <NetraAgentAvatar />
    <div>
      <header><strong>Netra</strong><span>Information agent</span><time><i aria-hidden="true" />Working now</time></header>
      <div className="portal-progress-copy"><span className="portal-progress-spinner" aria-hidden="true" /><div><strong>Checking {repositoryName}…</strong><small>Reading the repository and recent release information. I’ll post the answer here.</small></div></div>
    </div>
  </article>;
}

export function IntakeAnalysisProgress({ repository = "" }: { repository?: string }) {
  const repositoryName = repository.split("/").at(-1)?.replaceAll("-", " ") || "the selected product";
  return <article className="agent portal-message-progress" role="status" aria-live="polite">
    <NetraAgentAvatar />
    <div>
      <header><strong>Netra</strong><span>Preliminary analysis</span><time><i aria-hidden="true" />Working now</time></header>
      <div className="portal-progress-copy"><span className="portal-progress-spinner" aria-hidden="true" /><div><strong>Reviewing the complete request…</strong><small>Inspecting {repositoryName}, the conversation, links, and uploaded evidence before deciding what else is needed.</small></div></div>
    </div>
  </article>;
}

export function PortalMessageBody({ body }: { body: string }) {
  // Keep requester HTML inert while making only explicit http(s) URLs interactive.
  const url = /(https?:\/\/[^\s<>]+)/g;
  return <p>{body.split(url).map((part, index) => /^https?:\/\//.test(part)
    ? <a key={`${part}-${index}`} href={part} target="_blank" rel="noopener noreferrer">{part}</a>
    : <React.Fragment key={index}>{part}</React.Fragment>)}</p>;
}

function MessageAttachments({ attachments }: { attachments: PortalRequestDetail["attachments"] }) {
  if (attachments.length === 0) return null;
  return <div className="portal-message-attachments">{attachments.map((attachment) => <a className={attachment.content_type.startsWith("image/") ? "image" : "file"} key={attachment.id} href={attachment.url} target="_blank" rel="noopener noreferrer">{attachment.content_type.startsWith("image/") ? <img src={attachment.url} alt={attachment.file_name} loading="lazy" /> : <span aria-hidden="true">⌕</span>}<strong>{attachment.file_name}</strong><small>{(attachment.size_bytes / 1024).toFixed(0)} KB</small></a>)}</div>;
}

export function PlanPanel({ request, selected, selectedVersion, setSelectedVersion, planView, setPlanView, busy, onApprove }: {
  request: PortalRequestDetail;
  selected?: RequestPlan;
  selectedVersion: number;
  setSelectedVersion: (version: number) => void;
  planView: "plan" | "diff";
  setPlanView: (view: "plan" | "diff") => void;
  busy: string;
  onApprove: () => void;
}) {
  const priorPlan = selected
    ? request.plans.filter((plan) => plan.version < selected.version).at(-1)
    : undefined;
  return <section className="portal-panel portal-plan">
    <div className="portal-panel-heading">
      <div><p className="portal-eyebrow">VERSIONED PLAN</p><h2>{selected?.title ?? "Implementation plan"}</h2></div>
      <div className="portal-plan-controls">
        {request.plans.length > 0 && <select aria-label="Plan version" value={selectedVersion} onChange={(event) => setSelectedVersion(Number(event.target.value))}>{[...request.plans].reverse().map((plan) => <option key={plan.version} value={plan.version}>v{plan.version} · {plan.status.replaceAll("_", " ")}</option>)}</select>}
        <div className="portal-segmented"><button className={planView === "plan" ? "active" : ""} onClick={() => setPlanView("plan")}>Plan</button><button className={planView === "diff" ? "active" : ""} onClick={() => setPlanView("diff")}>Diff</button></div>
      </div>
    </div>
    {!selected ? <div className="portal-plan-empty"><p>Complete the structured requirements. Netra creates a versioned plan as soon as the deterministic intake gate is satisfied.</p></div> : <>
      <div className="portal-plan-meta"><span className={`portal-plan-status ${selected.status}`}>{selected.status.replaceAll("_", " ")}</span><span>Created {formatRelativeTime(selected.created_at)}</span>{selected.approved_by && <span>Approved by {selected.approved_by.name}</span>}</div>
      {selected.summary && <p className="portal-plan-summary">{selected.summary}</p>}
      <div className="portal-plan-document">{planView === "plan" ? selected.content : selected.change_summary || planLineDiff(selected.content, priorPlan?.content)}</div>
      {canApprovePlan(request, selected.version) && <div className="portal-approval-bar"><div><strong>Ready for your approval</strong><span>Approval starts this exact sequence: create or update the GitHub issue, reproduce the reported failure, implement the fix, open one pull request, deploy its exact head, record verification, then return here for your review.</span></div><button className="portal-primary" disabled={Boolean(busy)} onClick={onApprove}>{busy === "approve" ? "Starting delivery…" : `Approve and start plan v${selected.version}`}</button></div>}
    </>}
  </section>;
}

export function EvidencePanel({ request, proof, videos, screenshots, reports, busy, onApprove, onError }: {
  request: PortalRequestDetail;
  proof: ReturnType<typeof exactHeadEvidence>;
  videos: PortalRequestDetail["artifacts"];
  screenshots: PortalRequestDetail["artifacts"];
  reports: PortalRequestDetail["artifacts"];
  busy?: string;
  onApprove?: () => void;
  onError?: (message: string) => void;
}) {
  const approved = requesterApprovedCurrentRevision(request);
  const qaStage = request.cycles.at(-1)?.stages?.find((stage) => stage.name === "QA");
  const qaFailure = qaStage && (qaStage.status === "failed" || (request.state === "failed" && qaStage.status !== "passed" && qaStage.detail))
    ? qaStage
    : undefined;
  return <section className="portal-panel portal-evidence">
    <div className="portal-panel-heading"><div><p className="portal-eyebrow">VERIFICATION EVIDENCE</p><h2>Proof of this revision</h2></div>{proof.headSha && <code>{compactSha(proof.headSha)}</code>}</div>
    <div className={`portal-proof ${proof.exact ? "exact" : "stale"}`}><span aria-hidden="true">{proof.exact ? "✓" : "!"}</span><div><strong>{proof.exact ? "Exact-head evidence" : qaFailure ? "Automated QA needs attention" : "Evidence not ready"}</strong><p>{qaFailure?.detail ?? proof.reason}</p></div></div>
    <div className="portal-review-handoff"><div><strong>Confirm the fix yourself</strong><span>Try the acceptance steps in the exact-head preview, then record your decision here. Netra’s automated QA remains a separate merge gate.</span></div><div className="portal-review-actions">{request.preview?.url && request.preview.exact_head ? <a className="portal-secondary" href={request.preview.url} target="_blank" rel="noreferrer">Open preview dashboard ↗</a> : <span className="portal-muted">Preview dashboard not available for this revision.</span>}{approved ? <span className="portal-review-approved">✓ Looks good recorded for {compactSha(request.requester_review?.head_sha)}</span> : onApprove && <button className="portal-primary" disabled={Boolean(busy) || !canAcceptRequest(request)} title={canAcceptRequest(request) ? "Record that this exact preview passed your QA pass." : "An active exact-head preview is required."} onClick={onApprove}>{busy === "accept" ? "Recording…" : "Looks good"}</button>}</div></div>
    {request.qa_run && <div className="portal-test-summary"><div><span>QA status</span><strong>{request.qa_run.status}</strong></div><div><span>Passed</span><strong>{request.qa_run.passed}</strong></div><div><span>Failed</span><strong>{request.qa_run.failed}</strong></div><div><span>Skipped</span><strong>{request.qa_run.skipped}</strong></div></div>}
    {request.qa_run?.steps?.length ? <div className="portal-qa-steps">{request.qa_run.steps.map((step, index) => <div key={step.id ?? `${step.title}-${index}`}><span className={step.status}>{step.status === "passed" ? "✓" : step.status === "failed" ? "×" : "·"}</span><p><strong>{step.title}</strong>{step.detail && <small>{step.detail}</small>}</p>{step.duration_ms !== undefined && <time>{(step.duration_ms / 1000).toFixed(1)}s</time>}</div>)}</div> : null}
    {videos.map((artifact) => <article className="portal-video" key={artifact.id}><div><strong>{artifact.title}</strong><span>Recorded against <code>{compactSha(artifact.head_sha)}</code></span></div><PrivateEvidenceMedia artifact={artifact} kind="video" onError={onError ?? (() => {})} /></article>)}
    {screenshots.length > 0 && <div className="portal-screenshot-grid">{screenshots.map((artifact) => <figure key={artifact.id}><PrivateEvidenceMedia artifact={artifact} kind="screenshot" onError={onError ?? (() => {})} /><figcaption><strong>{artifact.title}</strong><span>{compactSha(artifact.head_sha)}</span></figcaption></figure>)}</div>}
    {reports.length > 0 && <div className="portal-report-list">{reports.map((artifact) => <button key={artifact.id} onClick={() => void openArtifact(artifact.id, onError ?? (() => {}))}><span>{artifact.kind === "test_report" ? "✓" : "◇"}</span><p><strong>{artifact.title}</strong><small>{artifact.summary ?? artifact.kind.replaceAll("_", " ")}</small></p><code>{compactSha(artifact.head_sha)}</code></button>)}</div>}
    {!request.qa_run && request.artifacts.length === 0 && <EmptyState title={qaFailure ? "Verification did not complete" : "Verification has not run"} body={qaFailure ? "An operator can use Retry QA above. Your Looks good decision remains attached to this exact revision." : "Evidence appears here after Netra deploys and tests an approved revision."} />}
  </section>;
}

export function RequirementsPanel({ request }: { request: PortalRequestDetail }) {
  const requirements = request.requirements;
  const facts = requirements ? [
    ["Problem", requirements.problem], ["Reproduction", requirements.reproduction],
    ["Expected", requirements.expected_behavior], ["Actual", requirements.actual_behavior],
    ["Impact", requirements.impact], ["Environment", requirements.environment], ["Scope", requirements.scope],
  ].filter((item): item is [string, string] => Boolean(item[1])) : [];
  return <section className="portal-panel portal-requirements">
    <div className="portal-requirements-heading"><div><p className="portal-eyebrow">REQUIREMENTS</p><h2>What we know</h2></div></div>
    {request.state === "needs_input" && <p className="portal-intake-note">Netra is gathering the remaining details in the conversation, one question at a time.</p>}
    {facts.length === 0 ? <p className="portal-muted">No structured details yet.</p> : <dl>{facts.map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>)}</dl>}{requirements?.acceptance_criteria?.length ? <><h3>Acceptance checks</h3><ul>{requirements.acceptance_criteria.map((check) => <li key={check}><span>✓</span>{check}</li>)}</ul></> : null}
  </section>;
}

function DeliveryPanel({ request, proof }: { request: PortalRequestDetail; proof: ReturnType<typeof exactHeadEvidence> }) {
  return <section className="portal-panel portal-delivery"><p className="portal-eyebrow">DELIVERY</p><h2>Current revision</h2><dl>
    <dt>Pull request</dt><dd>{request.github?.pull_request_number ? `#${request.github.pull_request_number}` : "Not created"}</dd>
    <dt>Head commit</dt><dd><code>{compactSha(proof.headSha)}</code></dd>
    <dt>Preview</dt><dd>{request.preview?.status ?? "Pending"}</dd>
    <dt>QA</dt><dd>{request.qa_run?.status ?? "Pending"}</dd>
    <dt>Merge state</dt><dd>{request.github?.mergeable_state?.replaceAll("_", " ") ?? "Unknown"}</dd>
  </dl>{request.preview?.url && <a className={`portal-preview-link ${request.preview.exact_head ? "" : "disabled"}`} href={request.preview.exact_head ? request.preview.url : undefined} target="_blank" rel="noreferrer"><span>Open preview dashboard</span><strong>↗</strong></a>}</section>;
}

export function DeliveryStage({ request, proof }: { request: PortalRequestDetail; proof: ReturnType<typeof exactHeadEvidence> }) {
  const cycle = request.cycles.at(-1);
  const stages = cycle?.stages ?? [];
  return <div className="portal-delivery-stage">
    <section className="portal-panel portal-stage-hero">
      <p className="portal-eyebrow">DELIVERY</p>
      <h2>{request.state === "building" ? "Netra is implementing the approved plan" : request.state === "previewing" ? "The pull request is being deployed" : "The current revision is ready for verification"}</h2>
      <p>Follow the managed issue, pull request, preview deployment, and automated gates here. There is no requester decision on this step.</p>
    </section>
    <section className="portal-delivery-cards" aria-label="Delivery status">
      <article><span>Issue</span><strong>{request.github?.issue_number ? `#${request.github.issue_number}` : "Pending"}</strong>{request.github?.issue_url && <a href={request.github.issue_url} target="_blank" rel="noreferrer">Open issue ↗</a>}</article>
      <article><span>Pull request</span><strong>{request.github?.pull_request_number ? `#${request.github.pull_request_number}` : "Pending"}</strong>{request.github?.pull_request_url && <a href={request.github.pull_request_url} target="_blank" rel="noreferrer">Open PR ↗</a>}</article>
      <article><span>Exact head</span><strong><code>{compactSha(proof.headSha)}</code></strong><small>{request.preview?.exact_head ? "Preview matches" : "Waiting for exact preview"}</small></article>
      <article><span>Automated QA</span><strong>{request.qa_run?.status ?? stages.find((stage) => stage.name === "QA")?.status ?? "Pending"}</strong><small>{request.qa_run ? `${request.qa_run.passed} passed · ${request.qa_run.failed} failed` : "Runs after preview deployment"}</small></article>
    </section>
    {stages.length > 0 && <section className="portal-panel portal-delivery-timeline"><div className="portal-panel-heading"><div><p className="portal-eyebrow">CURRENT CYCLE</p><h2>Delivery gates</h2></div><span>Cycle {cycle?.number}</span></div><div>{stages.map((stage) => <article key={stage.name}><i className={stage.status} aria-hidden="true">{stage.status === "passed" ? "✓" : stage.status === "failed" ? "×" : stage.status === "running" ? "…" : "·"}</i><p><strong>{stage.name}</strong><span>{stage.detail ?? stage.status.replaceAll("_", " ")}</span></p></article>)}</div></section>}
  </div>;
}

/// A repository with no pull-request checks is reported as `none`. That is a
/// satisfied gate, not a passing check, and it is labelled as such.
function ciGateTone(status?: string) {
  if (status === "passed" || status === "none") return "passed";
  if (status === "failed") return "failed";
  return "pending";
}

function ciGateCopy(status?: string) {
  if (status === "none") return "This repository runs no checks on pull requests, so there is nothing to wait for.";
  if (status === "passed") return "Repository checks passed on this head.";
  if (status === "failed") return "Repository checks failed on this head.";
  if (status === "running") return "Repository checks are still running on this head.";
  return "Repository checks have not reported for this head yet.";
}

export function MergeStage({ request, proof, user, busy, onMerge }: {
  request: PortalRequestDetail;
  proof: ReturnType<typeof exactHeadEvidence>;
  user?: PortalUser | null;
  busy?: string;
  onMerge?: (method: MergeMethod) => void;
}) {
  const merged = request.state === "merged";
  const ready = request.state === "merge_ready";
  const blocked = request.state === "merge_blocked";
  const gates = outstandingMergeGates(request);
  // Waiting on automation is not the same as being blocked, and saying "blocked"
  // for a revision whose gates are simply still running is what made the merge
  // step read like a dead end.
  const waiting = !merged && !ready && !blocked;
  const canMergeHere = Boolean(onMerge) && ready && user?.role === "admin";
  const [method, setMethod] = useState<MergeMethod>("squash");
  return <div className="portal-merge-stage">
    <section className={`portal-panel portal-stage-hero ${merged || ready ? "success" : blocked ? "blocked" : ""}`}>
      <p className="portal-eyebrow">FINAL HANDOFF</p>
      <h2>{merged ? "This revision has been merged" : ready ? "All gates passed. The pull request is ready to merge." : blocked ? "The final merge is blocked" : "Waiting on the remaining gates"}</h2>
      <p>{merged
        ? "Netra has retained the request, exact-head evidence, and decision history."
        : ready
          ? "A Netra admin can merge this exact revision here, or you can review and merge the same pull request in GitHub."
          : blocked
            ? "Resolve the reported merge conflict or failed gate before completing the final handoff."
            : "Your decision is recorded. The merge opens as soon as the automated gates below finish against this same head."}</p>
      <div className="portal-merge-actions">
        {canMergeHere && <>
          <label className="portal-merge-method"><span>Merge method</span><select value={method} onChange={(event) => setMethod(event.target.value as MergeMethod)}><option value="squash">Squash and merge</option><option value="merge">Merge commit</option><option value="rebase">Rebase and merge</option></select></label>
          <button className="portal-primary" disabled={Boolean(busy)} onClick={() => onMerge?.(method)}>{busy === "merge" ? "Merging…" : `Merge PR #${request.github?.pull_request_number} in Netra`}</button>
        </>}
        {request.github?.pull_request_url && <a className={ready && !canMergeHere ? "portal-primary" : "portal-secondary"} href={request.github.pull_request_url} target="_blank" rel="noreferrer">{ready ? "Open PR in GitHub ↗" : "Open pull request ↗"}</a>}
      </div>
    </section>
    {waiting && gates.length > 0 && <section className="portal-panel portal-merge-waiting"><p className="portal-eyebrow">STILL RUNNING</p><h2>What the merge is waiting for</h2><ul>{gates.map((gate) => <li key={gate}><span aria-hidden="true">·</span>{gate}</li>)}</ul></section>}
    <section className="portal-panel portal-merge-checklist"><div className="portal-panel-heading"><div><p className="portal-eyebrow">READINESS</p><h2>Exact revision gates</h2></div><code>{compactSha(proof.headSha)}</code></div><div>
      <p><i className={ciGateTone(request.cycles.at(-1)?.ci_status)}>{ciGateTone(request.cycles.at(-1)?.ci_status) === "passed" ? "✓" : ciGateTone(request.cycles.at(-1)?.ci_status) === "failed" ? "×" : "·"}</i><span><strong>Repository checks</strong><small>{ciGateCopy(request.cycles.at(-1)?.ci_status)}</small></span></p>
      <p><i className={request.preview?.exact_head ? "passed" : "pending"}>{request.preview?.exact_head ? "✓" : "·"}</i><span><strong>Exact-head preview</strong><small>The preview matches the current pull-request head.</small></span></p>
      <p><i className={request.qa_run?.status === "passed" ? "passed" : "pending"}>{request.qa_run?.status === "passed" ? "✓" : "·"}</i><span><strong>Automated QA</strong><small>Repository and exploratory checks passed.</small></span></p>
      <p><i className={requesterApprovedCurrentRevision(request) ? "passed" : "pending"}>{requesterApprovedCurrentRevision(request) ? "✓" : "·"}</i><span><strong>Requester QA</strong><small>Looks good was recorded against this exact head.</small></span></p>
      <p><i className={proof.exact ? "passed" : "pending"}>{proof.exact ? "✓" : "·"}</i><span><strong>Durable evidence</strong><small>The fix recording and reports are retained.</small></span></p>
    </div></section>
  </div>;
}

export function DeployStage({ request }: { request: PortalRequestDetail }) {
  const deployment = request.deployment;
  const status = deployment?.status ?? "pending";
  const environment = deployment?.environment ?? "target environment";
  const successful = status === "success";
  const failed = status === "failed";
  const merged = ["merged", "closed"].includes(request.state);
  const production = environmentTier(request.deployments, "production");
  // "Live" means production specifically. The summary evidence describes whichever
  // environment the merge itself deployed to, so it only stands in when Netra has
  // not observed any tier for this repository.
  const live = (request.deployments ?? []).length > 0 ? deploymentReached(production) : successful;
  const acceptance = request.requirements?.acceptance_criteria ?? [];
  return <div className="portal-deploy-stage">
    {/* The point of this step is the answer to "is my problem fixed?", so that
        comes first, before any provider detail. */}
    <section className={`portal-panel portal-stage-hero ${merged ? "success" : failed ? "blocked" : ""}`}>
      <p className="portal-eyebrow">OUTCOME</p>
      <h2>{merged
        ? live ? "Fixed, merged, and live" : "Fixed and merged"
        : "This revision has not been merged yet"}</h2>
      <p>{merged
        ? live
          ? `The fix you approved is merged and has reached ${production?.environment ?? environment}.`
          : "The fix you approved is merged. Netra is following it to each environment below."
        : "The deploy step fills in once the pull request is merged."}</p>
      <div className="portal-merge-actions">
        {request.github?.pull_request_url && <a className="portal-secondary" href={request.github.pull_request_url} target="_blank" rel="noreferrer">Merged pull request ↗</a>}
        {deployment?.url && <a className="portal-secondary" href={deployment.url} target="_blank" rel="noreferrer">Deployment evidence ↗</a>}
      </div>
    </section>
    <section className="portal-panel portal-environment-progress"><div className="portal-panel-heading"><div><p className="portal-eyebrow">WHERE IT HAS REACHED</p><h2>Environments</h2></div><code>{compactSha(deployment?.sha ?? request.github?.head_sha)}</code></div>
      <div className="portal-environment-list">{DEPLOYMENT_TIERS.map(({ tier, label }) => {
        const summary = deploymentTierSummary(environmentTier(request.deployments, tier), label);
        return <article key={tier} className={`portal-environment ${summary.tone}`}>
          <i aria-hidden="true">{summary.mark}</i>
          <p><strong>{summary.text}</strong><small>{summary.detail}</small></p>
        </article>;
      })}</div>
    </section>
    {acceptance.length > 0 && <section className="portal-panel portal-acceptance-recap"><p className="portal-eyebrow">WHAT WAS ACCEPTED</p><h2>Acceptance checks for this request</h2><ul>{acceptance.map((check) => <li key={check}><span aria-hidden="true">✓</span>{check}</li>)}</ul></section>}
    <section className="portal-delivery-cards" aria-label="Deployment evidence">
      <article><span>Deployment status</span><strong>{status.replaceAll("_", " ")}</strong><small>{successful ? "Provider-confirmed" : status === "unavailable" ? "No deployment provider reported this repository" : "Netra will keep checking"}</small></article>
      <article><span>Environment</span><strong>{environment}</strong><small>Derived from the deployment or merge target</small></article>
      <article><span>Merge commit</span><strong><code>{compactSha(deployment?.sha)}</code></strong><small>Deployment evidence is tied to this revision</small></article>
      <article><span>Provider</span><strong>{deployment?.provider ?? "Pending"}</strong><small>{deployment?.deployed_at ? new Date(deployment.deployed_at).toLocaleString() : "Completion time pending"}</small></article>
    </section>
  </div>;
}

export function StepFooter({ request, activeStep, onSelect }: { request: PortalRequestDetail; activeStep: RequestStep; onSelect: (step: RequestStep) => void }) {
  const index = REQUEST_STEP_META.findIndex((step) => step.key === activeStep);
  const previous = REQUEST_STEP_META[index - 1];
  const next = REQUEST_STEP_META[index + 1];
  const furthest = furthestRequestStep(request);
  const furthestIndex = REQUEST_STEP_META.findIndex((step) => step.key === furthest);
  return <nav className="portal-step-footer" aria-label="Request step navigation">
    <div>{previous && <button className="portal-secondary" onClick={() => onSelect(previous.key)}>← Previous: {previous.label}</button>}</div>
    <span>Step {index + 1} of {REQUEST_STEP_META.length}</span>
    <div>{next && <button className="portal-primary" disabled={index + 1 > furthestIndex} title={index + 1 > furthestIndex ? `${next.label} becomes available when this stage is complete.` : undefined} onClick={() => onSelect(next.key)}>Next: {next.label} →</button>}</div>
  </nav>;
}

const MERGE_DESK_SHELVES = [
  { key: "open", label: "Needs a human", states: ["checking", "blocked", "conflicting"], blurb: "Managed pull requests still working through their gates or waiting on a fix." },
  { key: "ready", label: "Ready to merge", states: ["ready"], blurb: "Every exact-head gate passed and the requester approved this revision." },
  { key: "merged", label: "Merged", states: ["merged"], blurb: "Merged and on the way out. Netra is following each revision to its environments." },
  { key: "live", label: "Live", states: ["live"], blurb: "Already in production. Nothing here needs a decision." },
] as const;

type MergeDeskShelf = typeof MERGE_DESK_SHELVES[number]["key"];

export function MergeDesk({ user, onError }: { user?: PortalUser | null; onError: (error: string) => void }) {
  const [items, setItems] = useState<MergeReadinessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [shelf, setShelf] = useState<MergeDeskShelf>("ready");
  const [pending, setPending] = useState<MergeReadinessItem | null>(null);
  const [method, setMethod] = useState<MergeMethod>("squash");
  const [merging, setMerging] = useState("");
  const refresh = useCallback(async () => {
    try { setItems(await portalApi.listMergeReadiness()); onError(""); }
    catch (reason) { onError(errorMessage(reason)); }
    finally { setLoading(false); }
  }, [onError]);
  useEffect(() => { void refresh(); const timer = window.setInterval(() => void refresh(), 15_000); return () => window.clearInterval(timer); }, [refresh]);
  const counts = (states: readonly string[]) => items.filter((item) => states.includes(item.state)).length;
  const active = MERGE_DESK_SHELVES.find((entry) => entry.key === shelf) ?? MERGE_DESK_SHELVES[0];
  const filtered = items.filter((item) => (active.states as readonly string[]).includes(item.state));
  const shipped = shelf === "merged" || shelf === "live";
  const merge = async (item: MergeReadinessItem) => {
    setMerging(item.request_id);
    try {
      await portalApi.mergeRequest(item.request_id, method);
      setPending(null);
      onError("");
      await refresh();
      setShelf("merged");
    } catch (reason) {
      onError(errorMessage(reason));
    } finally {
      setMerging("");
    }
  };
  const mergeTableHead = shipped
    ? <tr><th>Request</th><th>Revision</th><th>Test</th><th>Prod</th><th>Status</th><th /></tr>
    : <tr><th>Request</th><th>Revision</th><th>CI</th><th>Preview</th><th>QA</th><th>Requester QA</th><th>Conflict</th><th>Readiness</th><th /></tr>;

  return <>
    <section className="portal-heading"><div><p className="portal-eyebrow">HUMAN MERGE AUTHORITY</p><h1>Merge desk</h1><p>One view of exact-head proof, protection gates, and conflicts. Netra merges only when an admin here asks it to.</p></div><button className="portal-secondary" onClick={() => void refresh()}>Refresh checks</button></section>
    {/* A figure here is a count of pull requests, and before the check returns
        there is no count — only an empty list that has not been filled in yet.
        Printing "0 blocked or conflicting" over it is the merge desk saying
        nothing is wrong, which is the one thing it must never say by default. */}
    <section className="portal-merge-metrics" aria-busy={loading ? "true" : undefined}>
      <div><span>Managed pull requests</span><strong>{loading ? <PortalSkeletonBar width={34} height={19} /> : items.length}</strong></div>
      <div className="green"><span>Ready to merge</span><strong>{loading ? <PortalSkeletonBar width={34} height={19} /> : counts(["ready"])}</strong></div>
      <div className="red"><span>Blocked or conflicting</span><strong>{loading ? <PortalSkeletonBar width={34} height={19} /> : counts(["blocked", "conflicting"])}</strong></div>
      <div><span>Merged, not yet live</span><strong>{loading ? <PortalSkeletonBar width={34} height={19} /> : counts(["merged"])}</strong></div>
      <div className="green"><span>Live in production</span><strong>{loading ? <PortalSkeletonBar width={34} height={19} /> : counts(["live"])}</strong></div>
    </section>
    <section className="portal-panel portal-merge-panel">
      <div className="portal-panel-heading"><div><p className="portal-eyebrow">{shipped ? "SHIPPED WORK" : "READINESS QUEUE"}</p><h2>{active.label}</h2><p className="portal-muted">{active.blurb}</p></div><div className="portal-segmented">{MERGE_DESK_SHELVES.map((entry) => <button key={entry.key} className={shelf === entry.key ? "active" : ""} onClick={() => { setShelf(entry.key); setPending(null); }}>{entry.label} <small>{counts(entry.states)}</small></button>)}</div></div>
      {/* The header is known from the shelf, not from the data, so the table
          draws itself and fills in — rather than a spinner being swapped for a
          table of a shape the reader has not seen yet. */}
      {loading ? <div className="portal-table-wrap"><table>
        <thead>{mergeTableHead}</thead>
        <tbody><PortalRowsSkeleton label="Checking pull requests against GitHub" columns={shipped ? 6 : 9} /></tbody>
      </table></div> : filtered.length === 0 ? <EmptyState title={`Nothing in ${active.label.toLowerCase()}`} body={shipped ? "Merged and live requests appear here after their merge." : "Managed pull requests appear after an approved plan begins delivery."} /> : <div className="portal-table-wrap"><table>
        <thead>{mergeTableHead}</thead>
        <tbody>{filtered.map((item) => <tr key={`${item.repository}-${item.pull_request_number}`}>
          <td><button className="portal-table-link" onClick={() => navigate(portalRequestPath(item.request_id))}><strong>{item.title}</strong><span>{item.repository} · PR #{item.pull_request_number}</span></button></td>
          <td><code>{compactSha(item.head_sha)}</code><span>{item.base_drift === undefined ? "Drift unknown" : item.base_drift > 0 ? `${item.base_drift} commit${item.base_drift === 1 ? "" : "s"} behind` : "Up to date"}</span></td>
          {shipped ? <>
            <EnvironmentCell item={item} tier="test" label="Test" />
            <EnvironmentCell item={item} tier="production" label="Prod" />
            <td><span className={`portal-readiness ${item.state}`}>{item.state === "live" ? "live in production" : "merged"}</span></td>
          </> : <>
            <GateCell state={item.ci_status} />
            <GateCell state={item.preview_status} />
            <GateCell state={item.qa_status} />
            <td><strong>{item.requester_approved_head_sha === item.head_sha ? "Approved" : "Not approved"}</strong><span>{item.requester_approved_head_sha === item.head_sha ? item.requester_approved_by ?? "requester" : "waiting on this exact head"}</span></td>
            <td><strong>{item.state === "conflicting" ? "Conflict" : "Clear"}</strong><span>{item.conflict_attempts ?? 0} repair attempt{item.conflict_attempts === 1 ? "" : "s"}</span>{item.conflict_detail && <small>{item.conflict_detail}</small>}</td>
            <td><span className={`portal-readiness ${item.state}`}>{item.state.replaceAll("_", " ")}</span>{item.unresolved_threads ? <small>{item.unresolved_threads} unresolved threads</small> : null}</td>
          </>}
          <td><div className="portal-row-actions">
            {canMergeFromPortal(user ?? null, item) && <button className="portal-primary small" disabled={Boolean(merging)} onClick={() => { setPending(item); }}>Merge…</button>}
            {item.pull_request_url && <a className="portal-secondary small" href={item.pull_request_url} target="_blank" rel="noreferrer">GitHub ↗</a>}
            {!shipped && item.state !== "ready" && <button className="portal-secondary small" onClick={() => navigate(portalRequestPath(item.request_id))}>Review block</button>}
          </div></td>
        </tr>)}</tbody>
      </table></div>}
    </section>
    {pending && <MergeConfirmation item={pending} method={method} setMethod={setMethod} busy={merging === pending.request_id} onCancel={() => setPending(null)} onConfirm={() => void merge(pending)} />}
  </>;
}

function EnvironmentCell({ item, tier, label }: { item: MergeReadinessItem; tier: string; label: string }) {
  const summary = deploymentTierSummary(environmentTier(item.deployments, tier), label);
  return <td className={`portal-gate-cell ${summary.tone}`} title={summary.detail}><i aria-hidden="true">{summary.mark}</i><span>{summary.text}</span></td>;
}

/// Everything an admin needs to decide, in one place, before an irreversible step.
export function MergeConfirmation({ item, method, setMethod, busy, onCancel, onConfirm }: {
  item: MergeReadinessItem;
  method: MergeMethod;
  setMethod: (method: MergeMethod) => void;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const approved = item.requester_approved_head_sha === item.head_sha;
  return <section className="portal-panel portal-merge-confirm" role="dialog" aria-label="Confirm merge">
    <div className="portal-panel-heading"><div><p className="portal-eyebrow">CONFIRM MERGE</p><h2>{item.title}</h2><p className="portal-muted">{item.repository} · PR #{item.pull_request_number} · <code>{compactSha(item.head_sha)}</code></p></div><button className="portal-secondary" onClick={onCancel}>Cancel</button></div>
    <dl className="portal-merge-evidence">
      <dt>Repository checks</dt><dd>{item.ci_status === "none" ? "No checks configured for pull requests" : item.ci_status ?? "unknown"}</dd>
      <dt>Preview</dt><dd>{item.preview_status ?? "unknown"}{item.exact_head_verified ? " · exact head" : " · head not verified"}</dd>
      <dt>Automated QA</dt><dd>{item.qa_status ?? "unknown"}</dd>
      <dt>Requester QA</dt><dd>{approved ? `Looks good from ${item.requester_approved_by ?? "the requester"}${item.requester_approved_at ? ` ${formatRelativeTime(item.requester_approved_at)}` : ""}` : "Not recorded for this head"}</dd>
      <dt>Base branch</dt><dd>{item.base_drift === undefined ? "Drift unknown" : item.base_drift > 0 ? `${item.base_drift} commit${item.base_drift === 1 ? "" : "s"} behind` : "Up to date"} · {(item.mergeable_state ?? "unknown").replaceAll("_", " ").toLowerCase()}</dd>
      <dt>Conflict repairs</dt><dd>{item.conflict_attempts ?? 0}</dd>
    </dl>
    <p className="portal-merge-note">Netra merges the exact head <code>{compactSha(item.head_sha)}</code>. If the pull request moved since these gates were recorded, GitHub rejects the merge and the gates re-run.</p>
    <div className="portal-merge-actions">
      <label className="portal-merge-method"><span>Merge method</span><select value={method} onChange={(event) => setMethod(event.target.value as MergeMethod)}><option value="squash">Squash and merge</option><option value="merge">Merge commit</option><option value="rebase">Rebase and merge</option></select></label>
      <button className="portal-primary" disabled={busy || !approved} onClick={onConfirm}>{busy ? "Merging…" : `Merge PR #${item.pull_request_number}`}</button>
      {item.pull_request_url && <a className="portal-secondary" href={item.pull_request_url} target="_blank" rel="noreferrer">Review in GitHub ↗</a>}
    </div>
  </section>;
}

function GateCell({ state }: { state?: string }) {
  const normalized = state?.toLowerCase() ?? "pending";
  const good = ["passed", "active", "ready", "success", "succeeded", "none"].includes(normalized);
  const bad = ["failed", "stale", "error", "blocked"].includes(normalized);
  return <td className={`portal-gate-cell ${good ? "good" : bad ? "bad" : "pending"}`} title={normalized === "none" ? "This repository runs no checks on pull requests." : undefined}><i aria-hidden="true">{good ? "✓" : bad ? "×" : "·"}</i><span>{normalized === "none" ? "no checks" : normalized.replaceAll("_", " ")}</span></td>;
}

export function RepositorySettings({ repositories, onChanged, onError }: {
  repositories: PortalRepository[];
  onChanged: () => Promise<void>;
  onError: (error: string) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, PortalRepository>>({});
  const [newDraft, setNewDraft] = useState<PortalRepository>(() => newPortalRepository());
  const [adding, setAdding] = useState(repositories.length === 0);
  const [saving, setSaving] = useState("");
  const [validationError, setValidationError] = useState("");
  useEffect(() => setDrafts(Object.fromEntries(repositories.map((repository) => [repository.repository, { ...repository }]))), [repositories]);
  useEffect(() => {
    if (repositories.length === 0) setAdding(true);
  }, [repositories.length]);
  const save = async (repository: PortalRepository, isNew = false) => {
    const normalized = normalizePortalRepository(repository);
    const invalid = validatePortalRepository(normalized);
    if (invalid) {
      setValidationError(invalid);
      return;
    }
    if (isNew && repositories.some((item) => item.repository.toLowerCase() === normalized.repository.toLowerCase())) {
      setValidationError("That repository already has portal settings.");
      return;
    }
    setValidationError("");
    setSaving(isNew ? "__new__" : normalized.repository);
    try {
      await portalApi.updateRepository(normalized.repository, {
        enabled: normalized.enabled,
        qa_config_path: normalized.qa_config_path ?? PORTAL_QA_CONFIG_PATH,
        secret_aliases: normalized.secret_aliases ?? [],
        retention_days: normalized.retention_days ?? 30,
        max_concurrency: normalized.max_concurrency ?? 1,
        updated_at: normalized.updated_at,
      });
      await onChanged();
      if (isNew) {
        setNewDraft(newPortalRepository());
        setAdding(false);
      }
      onError("");
    } catch (reason) { onError(errorMessage(reason)); }
    finally { setSaving(""); }
  };
  return <>
    <section className="portal-heading"><div><p className="portal-eyebrow">PILOT CONTROL</p><h1>Portal repositories</h1><p>Choose the small set of repositories staff can request work against and define their verification envelope.</p></div>{repositories.length > 0 && <button className="portal-primary" onClick={() => { setAdding((value) => !value); setValidationError(""); }}>{adding ? "Close" : "+ Add repository"}</button>}</section>
    <div className="portal-settings-callout"><span>1–3</span><div><strong>Start with a narrow pilot</strong><p>Expand only after exact-head evidence and workflow reliability meet the rollout gates.</p></div></div>
    {validationError && <div className="portal-banner error" role="alert"><span>{validationError}</span><button onClick={() => setValidationError("")} aria-label="Dismiss repository validation error">×</button></div>}
    <section className="portal-repository-grid">
      {adding && <RepositorySettingsCard
        repository={newDraft}
        isNew
        saving={saving === "__new__"}
        onChange={setNewDraft}
        onSave={() => void save(newDraft, true)}
      />}
      {repositories.map((repository) => {
        const draft = drafts[repository.repository] ?? repository;
        return <RepositorySettingsCard
          key={repository.repository}
          repository={draft}
          saving={saving === repository.repository}
          onChange={(value) => setDrafts((current) => ({ ...current, [repository.repository]: value }))}
          onSave={() => void save(draft)}
        />;
      })}
    </section>
  </>;
}

function RepositorySettingsCard({ repository, isNew = false, saving, onChange, onSave }: {
  repository: PortalRepository;
  isNew?: boolean;
  saving: boolean;
  onChange: (repository: PortalRepository) => void;
  onSave: () => void;
}) {
  return <article className={`portal-panel portal-repository-card${isNew ? " bootstrap" : ""}`}>
    <header><div><span className="portal-repository-icon">⌘</span><div><h2>{isNew ? "Add pilot repository" : repository.repository}</h2><p>{isNew ? "Register the first repository with execution disabled unless you explicitly enable it." : `Updated ${repository.updated_at ? formatRelativeTime(repository.updated_at) : "when saved"}`}</p></div></div><label className="portal-toggle"><input type="checkbox" checked={repository.enabled} onChange={(event) => onChange({ ...repository, enabled: event.target.checked })} /><span /><strong>{repository.enabled ? "Enabled" : "Disabled"}</strong></label></header>
    <div className="portal-settings-form">
      {isNew && <label><span>GitHub repository</span><input autoFocus value={repository.repository} placeholder="owner/repository" pattern="[A-Za-z0-9_.-]+/[A-Za-z0-9_.-]+" onChange={(event) => onChange({ ...repository, repository: event.target.value })} /></label>}
      <label><span>QA scenario file</span><input value={repository.qa_config_path ?? PORTAL_QA_CONFIG_PATH} onChange={(event) => onChange({ ...repository, qa_config_path: event.target.value })} /></label>
      <label><span>Artifact retention</span><div className="portal-input-suffix"><input type="number" min={1} max={3650} step={1} value={repository.retention_days ?? 30} onChange={(event) => onChange({ ...repository, retention_days: Number(event.target.value) })} /><span>days</span></div></label>
      <label><span>Concurrent workflows</span><input type="number" min={1} max={32} step={1} value={repository.max_concurrency ?? 1} onChange={(event) => onChange({ ...repository, max_concurrency: Number(event.target.value) })} /></label>
      <label><span>Allowed secret aliases</span><input value={(repository.secret_aliases ?? []).join(", ")} placeholder="preview_login, test_user" onChange={(event) => onChange({ ...repository, secret_aliases: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })} /></label>
    </div>
    <footer><span>Secrets are references only; values never enter portal responses.</span><button className="portal-primary" disabled={saving} onClick={onSave}>{saving ? "Saving…" : isNew ? "Add repository" : "Save repository"}</button></footer>
  </article>;
}
